import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './state/auth';
import './i18n/config';

// Components (not lazy loaded - needed immediately)
import AdminProtectedRoute from './components/AdminProtectedRoute';
import { NotificationListener } from './components/NotificationListener';

// Loading Component
const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Cargando...</p>
    </div>
  </div>
);

// Lazy load voter pages
const LoginPage = lazy(() => import('./pages/Login'));
const RegisterPage = lazy(() => import('./pages/Register'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));
const HomePage = lazy(() => import('./pages/Home'));
const ElectionListPage = lazy(() => import('./pages/ElectionList'));
const ElectionDetailsPage = lazy(() => import('./pages/ElectionDetails'));
const VotingBoothPage = lazy(() => import('./pages/VotingBooth'));
const ReceiptPage = lazy(() => import('./pages/Receipt'));
const VerifyReceiptPage = lazy(() => import('./pages/VerifyReceipt'));
const ResultsPage = lazy(() => import('./pages/Results'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));

// Lazy load admin pages
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminVoterManagement = lazy(() => import('./pages/AdminVoterManagement'));
const AdminElectionManagement = lazy(() => import('./pages/AdminElectionManagement'));
const AdminAuditLogs = lazy(() => import('./pages/AdminAuditLogs'));

/**
 * Protected Route wrapper
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Public Route wrapper (redirects to home if already authenticated)
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Route accessible by both voters and admins
 */
function PublicOrAuthenticatedRoute({ children }: { children: React.ReactNode }) {
  // Allow access without authentication (page will handle permissions internally)
  return <>{children}</>;
}

/**
 * Main App component with routing
 */
function App() {
  return (
    <BrowserRouter>
      <NotificationListener />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/elections"
          element={
            <ProtectedRoute>
              <ElectionListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/elections/:id"
          element={
            <ProtectedRoute>
              <ElectionDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/elections/:id/vote"
          element={
            <ProtectedRoute>
              <VotingBoothPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/receipt/:code"
          element={
            <ProtectedRoute>
              <ReceiptPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verify"
          element={
            <ProtectedRoute>
              <VerifyReceiptPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/elections/:id/results"
          element={
            <PublicOrAuthenticatedRoute>
              <ResultsPage />
            </PublicOrAuthenticatedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <Navigate to="/admin/dashboard" replace />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/voters"
          element={
            <AdminProtectedRoute>
              <AdminVoterManagement />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/elections"
          element={
            <AdminProtectedRoute>
              <AdminElectionManagement />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/elections/:id"
          element={
            <AdminProtectedRoute>
              <ElectionDetailsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <AdminProtectedRoute>
              <AdminAuditLogs />
            </AdminProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
